// src/pages/offlineSales/CustomerStatementPage.jsx
//
// Full customer ledger — opening balance, every transaction (invoices, payments received,
// credit notes, cash refunds) within a selectable date range, and a closing balance. Reached
// from ReceivablesPage.jsx's second action button. Backend: receivablesService.getCustomerStatement
// (rettzy-backend/src/modules/offlineSales/services/receivables.service.js).
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FileText, ArrowLeft, Printer, Receipt, Wallet, FileMinus, RotateCcw, Calendar, TrendingUp, Landmark, HandCoins, Undo2,
} from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import { PageHeader, DataTable } from '../../components/common';
import DateRangePicker from '../../components/ui/DateRangePicker/DateRangePicker';
import { getCustomerStatement } from '../../services/api/offlineSales/receivables';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import CustomerStatementPrintModal from './components/CustomerStatementPrintModal';
import styles from './CustomerStatementPage.module.css';

const TYPE_LABELS = {
  invoice: 'Invoice',
  payment: 'Payment Received',
  credit_note: 'Credit Note',
  return: 'Refund (Cash)',
  opening_balance: 'Opening Balance',
  customer_payment: 'Customer Payment',
  customer_payment_reversal: 'Payment Reversed',
};

const TYPE_ICONS = {
  invoice: Receipt,
  payment: Wallet,
  credit_note: FileMinus,
  return: RotateCcw,
  opening_balance: Landmark,
  customer_payment: HandCoins,
  customer_payment_reversal: Undo2,
};

const CustomerStatementPage = () => {
  const { customerId } = useParams();
  const { hasPermission } = usePermissions();
  const canReceivePayment = hasPermission('edit_offline_sales_order');
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { error: showError } = useAlert();

  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [showPrint, setShowPrint] = useState(false);

  const loadStatement = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getCustomerStatement(customerId, {
        dateFrom: dateRange.from || undefined,
        dateTo: dateRange.to || undefined,
      });
      setStatement(result);
    } catch (err) {
      showError(err?.response?.data?.message || 'Failed to load customer statement');
    } finally {
      setLoading(false);
    }
  }, [customerId, dateRange, showError]);

  useEffect(() => { loadStatement(); }, [loadStatement]);

  const columns = [
    {
      key: 'date',
      header: t('offlineSales.date'),
      render: (row) => <span className={styles.typeCell}><Calendar size={14} />{formatDate(row.date)}</span>,
    },
    {
      key: 'type',
      header: t('offlineSales.type'),
      render: (row) => {
        const TypeIcon = TYPE_ICONS[row.type] || FileText;
        const key = `offlineSales.transactionType_${row.type}`;
        const label = t(key);
        return (
          <span className={styles.typeCell}>
            <TypeIcon size={14} />
            {label && label !== key ? label : (TYPE_LABELS[row.type] || row.type)}
          </span>
        );
      },
    },
    {
      key: 'reference',
      header: t('offlineSales.reference'),
      render: (row) => {
        let ref = row.reference;
        if (row.type === 'invoice' || row.type === 'payment') {
          ref = <Link className={styles.docLink} to={`/offline-sales/orders/${row.orderId}`}>{row.reference}</Link>;
        } else if (row.type === 'credit_note') {
          ref = <Link className={styles.docLink} to={`/offline-sales/credit-notes/${row.creditNoteId}`}>{row.reference}</Link>;
        } else if (row.type === 'return') {
          ref = <Link className={styles.docLink} to={`/offline-sales/return-notes/${row.returnId}`}>{row.reference}</Link>;
        }
        if (row.type === 'customer_payment') {
          return (
            <div>
              <Link className={styles.docLink} to={`/offline-sales/customer-payments/${row.customerPaymentId}`}>{row.reference}</Link>
              <div className={styles.refMeta}>
                {[row.method, row.cheque?.isPostDated && `${t('offlineSales.chqPdcTag')} ${formatDate(row.cheque.chequeDate)}`, row.status === 'void' && t('offlineSales.cpStatus_void')]
                  .filter(Boolean).join(' · ')}
              </div>
            </div>
          );
        }
        const meta = row.type === 'payment' ? row.method
          : row.type === 'return' ? row.orderReference
            : row.type === 'customer_payment_reversal' ? row.reason : null;
        if (meta) {
          return (
            <div>
              {ref}
              <div className={styles.refMeta}>{meta}</div>
            </div>
          );
        }
        return ref;
      },
    },
    {
      key: 'debit',
      header: t('offlineSales.debit') || 'Debit',
      align: 'right',
      render: (row) => (row.debit > 0
        ? <span className={`${styles.amountCell} ${styles.debitAmount}`}>{formatCurrency(row.debit)}</span>
        : <span className={styles.mutedDash}>—</span>),
    },
    {
      key: 'credit',
      header: t('offlineSales.credit') || 'Credit',
      align: 'right',
      render: (row) => (row.credit > 0
        ? <span className={`${styles.amountCell} ${styles.creditAmount}`}>{formatCurrency(row.credit)}</span>
        : <span className={styles.mutedDash}>—</span>),
    },
    {
      key: 'runningBalance',
      header: t('offlineSales.balanceCol'),
      align: 'right',
      render: (row) => <span className={`${styles.amountCell} ${styles.balanceAmount}`}>{formatCurrency(row.runningBalance)}</span>,
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('offlineSales.customerStatement') || 'Customer Statement'}
        description={statement?.customer?.name && <span className={styles.customerName}>{statement.customer.name}</span>}
        icon={FileText}
        actions={
          <div className={styles.headerActions}>
            <button className={styles.secondaryBtn} onClick={() => navigate('/offline-sales/receivables')}>
              <ArrowLeft size={16} /> {t('common.back')}
            </button>
            {canReceivePayment && statement && !statement.customer?.isCashSales && (
              <button className={styles.primaryBtn} onClick={() => navigate(`/offline-sales/customer-payments/new?customerId=${customerId}`)}>
                <HandCoins size={15} /> {t('offlineSales.cpReceivePayment')}
              </button>
            )}
            {statement && (
              <button className={styles.secondaryBtn} onClick={() => setShowPrint(true)}>
                <Printer size={15} /> {t('common.printReceipt', 'Print Receipt')}
              </button>
            )}
          </div>
        }
      />

      <div className={styles.filterRow}>
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          placeholder={t('offlineSales.selectDateRange') || 'Select date range'}
        />
      </div>

      {statement && (
        <div className={styles.balanceCards}>
          <div className={styles.balanceCard}>
            <div className={styles.balanceCardLabel}><Wallet size={14} />{t('offlineSales.openingBalance') || 'Opening Balance'}</div>
            <div className={styles.balanceCardValue}>{formatCurrency(statement.openingBalance)}</div>
          </div>
          <div className={styles.balanceCard}>
            <div className={styles.balanceCardLabel}><TrendingUp size={14} />{t('offlineSales.closingBalance') || 'Closing Balance'}</div>
            <div className={`${styles.balanceCardValue} ${styles.balanceCardValueClosing}`}>{formatCurrency(statement.closingBalance)}</div>
          </div>
        </div>
      )}

      <DataTable
        data={statement?.transactions || []}
        columns={columns}
        loading={loading}
        emptyMessage={t('offlineSales.noTransactionsFound') || 'No transactions found'}
        emptyDescription={t('offlineSales.noTransactionsFoundDescription') || 'No transactions in the selected date range'}
      />

      {showPrint && statement && (
        <CustomerStatementPrintModal statement={statement} onClose={() => setShowPrint(false)} />
      )}
    </div>
  );
};

export default CustomerStatementPage;
